// composables/useAlgoliaInsights.ts
import aa from 'search-insights';
import { useVSFContext } from '@vue-storefront/core';
import { watch, computed, useContext } from '@nuxtjs/composition-api';
import { UseAlgoliaInsightsReturn } from './interfaces';
import { useSortingOptions } from '@vue-storefront/novulo-theme/composables/useSortingOptions';

export const useAlgoliaInsights = (): UseAlgoliaInsightsReturn => {
  const context = useVSFContext();
  const { $config } = useContext();
  const { indexName } = useSortingOptions();
  const ALGOLIA_PRODUCTS_CLICKED = 'algoliaProductClicked';
  const ALGOLIA_PRODUCTS_ADDED = 'algoliaProductsAdded';
  let isAAInitialized = false;

  const initAlgolia = () => {
    if (!isAAInitialized) {
      aa('init', {
        appId: $config.algoliaAppId,
        apiKey: $config.algoliaSearchKey,
        useCookie: context.$cookies.get('cookie_accepted') === 'true'
      });
      isAAInitialized = true;
    }
  };

  const userToken = computed(() => context.$cookies.get('N_user_token') || null);

  const setAuthenticatedUserToken = (token: string | null) => {
    if (token) {
      aa('setAuthenticatedUserToken', token);
    }
  };

  if (userToken) {
    setAuthenticatedUserToken(userToken.value);
  }

  watch(userToken, (newToken) => {
    if (newToken) {
      setAuthenticatedUserToken(newToken);
    }
  });

  const trackProductClick = ({ objectID, queryID, position }: { objectID: string, queryID?: string | null, position?: number | null }) => {

    initAlgolia();

    if (queryID) {
      aa('clickedObjectIDsAfterSearch', {
        eventName: 'Product Clicked',
        index: indexName,
        objectIDs: [objectID],
        positions: [position],
        queryID
      });
    } else {
      aa('clickedObjectIDs', {
        eventName: 'Product Clicked',
        index: indexName,
        objectIDs: [objectID]
      });
    }
  };

  const trackAddToCartPDP = ({ objectID, queryID }: { objectID: string, queryID?: string | null }) => {
    const eventPayload = {
      eventName: 'Added to Cart',
      eventSubtype: 'addToCart',
      index: indexName,
      objectIDs: [objectID]
    };
    initAlgolia();

    if (queryID) {
      aa('convertedObjectIDsAfterSearch', {...eventPayload, queryID});
    } else {
      aa('convertedObjectIDs', eventPayload);
    }
  };

  const trackAddToCartSRP = ({ objectID, queryID }: { objectID: string, queryID?: string | null }) => {
    const eventPayload = {
      eventName: 'Added to Cart',
      eventSubtype: 'addToCart',
      index: indexName,
      objectIDs: [objectID]
    };
    initAlgolia();

    if (queryID) {
      aa('addedToCartObjectIDsAfterSearch', {...eventPayload, queryID});
    } else {
      aa('addedToCartObjectIDs', eventPayload);
    }
  };

  const trackPurchase = ({
    objectIDs,
    objectData,
    currency
  }: {
    objectIDs: string[];
    objectData: {
      queryID?: string | null;
      price?: number;
      discount?: number | null;
      quantity: number;
    }[];
    currency: string;
  }) => {
    initAlgolia();

    // separate into two groups, with and without queryIDs
    const withIDs: string[] = [];
    const withData: typeof objectData = [];
    const withoutIDs: string[] = [];
    const withoutData: typeof objectData = [];

    objectData.forEach((item, idx) => {
      if (item.queryID) {
        withIDs.push(objectIDs[idx]);
        withData.push(item);
      } else {
        withoutIDs.push(objectIDs[idx]);
        withoutData.push(item);
      }
    });

    const sum = (data: typeof objectData) =>
      data.reduce((sum, it) => sum + ((it.price ?? 0) * it.quantity), 0);

    if (withIDs.length) {
      aa('purchasedObjectIDsAfterSearch', {
        eventName: 'Agolia Products Purchased',
        index: indexName,
        objectIDs: withIDs,
        objectData: withData,
        value: sum(withData),
        currency
      });

      withIDs.forEach(id =>
        removeFromSessionStorage(ALGOLIA_PRODUCTS_ADDED, id)
      );
    }

    if (withoutIDs.length) {
      aa('purchasedObjectIDs', {
        eventName: 'Products Purchased',
        index: indexName,
        objectIDs: withoutIDs,
        objectData: withoutData,
        value: sum(withoutData),
        currency
      });
    }
  };

  const trackFilterClick = ({ filter, category }: { filter: string, category: string }) => {
    initAlgolia();
    aa('clickedFilters', {
      eventName: `${category} Clicked`,
      index: indexName,
      filters: [`${category}:${filter}`]
    });
  };

  const trackFiltersEvent = (
    eventType: 'viewed' | 'converted',
    { filters }: { filters: { filter: string; category: string }[] }
  ) => {
    initAlgolia();

    const formattedFilters: string[] = filters.map(
      ({ filter, category }) => `${category}:${filter}`
    );

    if (eventType === 'converted') {
      aa('convertedFilters', {
        eventName: 'Filters Converted',
        index: indexName,
        filters: formattedFilters
      });
    }
    if (eventType === 'viewed') {
      aa('viewedFilters', {
        eventName: 'Filters Viewed',
        index: indexName,
        filters: formattedFilters
      });
    }
  };

  const trackProductsViewed = ({ objectIDs }: { objectIDs: string[] }) => {
    if (!objectIDs.length) return;

    initAlgolia();
    aa('viewedObjectIDs', {
      eventName: 'Products Viewed',
      index: indexName,
      objectIDs
    });
  };

  function addToSessionStorage(
    storageKey: string,
    productId: string,
    queryID: string | null
  ): void {
    let data: Record<string, string | null> = {};
    const raw = sessionStorage.getItem(storageKey);
    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch (e) {
        console.warn(`Unable to parse sessionStorage key "${storageKey}"`, e);
      }
    }

    data[productId] = queryID;
    sessionStorage.setItem(storageKey, JSON.stringify(data));
  }

  function fromSessionStorage(
    storageKey: string,
    productId: string
  ): string {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw || !productId) return '';

    try {
      const data: Record<string, any> = JSON.parse(raw);
      return Object.prototype.hasOwnProperty.call(data, productId) ? data[productId] : '';
    } catch (e) {
      return '';
    }
  }

  function removeFromSessionStorage(
    storageKey: string,
    productId: string
  ): void {
    let data: Record<string, string | null> = {};
    const raw = sessionStorage.getItem(storageKey);
    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch (e) {
        console.warn(`Unable to parse sessionStorage key "${storageKey}", clearing it instead`, e);

        return sessionStorage.removeItem(storageKey);
      }
    }

    if (Object.prototype.hasOwnProperty.call(data, productId)) {
      delete data[productId];

      if (Object.keys(data).length === 0) {
        sessionStorage.removeItem(storageKey);
      } else {
        sessionStorage.setItem(storageKey, JSON.stringify(data));
      }
    }
  }

  return {
    ALGOLIA_PRODUCTS_CLICKED,
    ALGOLIA_PRODUCTS_ADDED,
    addToSessionStorage,
    fromSessionStorage,
    removeFromSessionStorage,
    trackProductClick,
    trackAddToCartPDP,
    trackAddToCartSRP,
    trackPurchase,
    trackFilterClick,
    trackFiltersEvent,
    trackProductsViewed
  };
};
